import { useCallback } from 'react';

import { getMemberId } from '@trello/authentication';
import { client } from '@trello/graphql';
import { assertMutationSuccess } from '@trello/graphql-error-handling';
import { showFlag } from '@trello/nachos/experimental-flags';

import { TrelloSmartSchedulePreferredBoardsDocument } from './TrelloSmartSchedulePreferredBoardsQuery.generated';
import { TrelloUpdateSmartScheduleOnboardingBoardPreferenceDocument } from './TrelloUpdateSmartScheduleOnboardingBoardPreferenceMutation.generated';
import { TrelloUpdateSmartScheduleOnboardingProactiveStatusDocument } from './TrelloUpdateSmartScheduleOnboardingProactiveStatusMutation.generated';

interface SaveBoardPreferenceArgs {
  includeInbox?: boolean;
  preferredBoards?: string[];
  /**
   * When true, the internal error flag is suppressed so the caller can surface a
   * single consolidated error (used by the batched onboarding save).
   */
  silent?: boolean;
}

interface SaveProactiveStatusOptions {
  silent?: boolean;
}

/**
 * Persists Smart Schedule source and proactive preferences during onboarding,
 * mirroring the optimistic cache behavior used by the AI settings screen so the
 * board dropdown and proactive toggle stay in sync across the dialog.
 *
 * @returns Callbacks for saving the board/inbox source preference and the
 * proactive scheduling status. Each resolves to whether the save succeeded and
 * surfaces an error flag on failure rather than throwing.
 */
export const useUpsertSmartScheduleOnboardingPreferences = () => {
  const memberAri = getMemberId({ returnAri: true });

  const saveBoardPreference = useCallback(
    async ({
      includeInbox,
      preferredBoards,
      silent,
    }: SaveBoardPreferenceArgs): Promise<boolean> => {
      const cached = client.readQuery({
        query: TrelloSmartSchedulePreferredBoardsDocument,
        variables: { memberId: memberAri },
      });
      const currentSmartSchedule =
        cached?.trello?.member?.aiPreferences?.smartSchedule;
      const currentIncludeInbox = currentSmartSchedule?.includeInbox ?? false;
      const currentPreferredBoards =
        currentSmartSchedule?.preferredBoards ?? [];

      try {
        const result = await client.mutate({
          mutation: TrelloUpdateSmartScheduleOnboardingBoardPreferenceDocument,
          variables: {
            input: {
              includeInbox,
              preferredBoards,
            },
          },
          context: { traceId: null },
          optimisticResponse: {
            trello: {
              __typename: 'TrelloMutationApi',
              updateSmartScheduleBoardPreference: {
                __typename: 'TrelloUpdateSmartScheduleBoardPreferencePayload',
                success: true,
                errors: null,
                member: {
                  __typename: 'TrelloMember',
                  id: memberAri,
                  aiPreferences: {
                    __typename: 'TrelloMemberAiPreference',
                    smartSchedule: {
                      __typename: 'TrelloSmartSchedulePreference',
                      includeInbox: includeInbox ?? currentIncludeInbox,
                      preferredBoards:
                        preferredBoards ?? currentPreferredBoards,
                    },
                  },
                },
              },
            },
          },
        });

        assertMutationSuccess(
          result.data?.trello.updateSmartScheduleBoardPreference,
          result.error,
          'Failed to update smart schedule board preference',
        );

        return true;
      } catch {
        if (!silent) {
          showFlag({
            id: 'smartScheduleOnboardingBoardPreferenceError',
            title:
              "We weren't able to update your board preference. Please try again.",
            appearance: 'error',
            isAutoDismiss: false,
          });
        }

        return false;
      }
    },
    [memberAri],
  );

  const saveProactiveStatus = useCallback(
    async (
      enabled: boolean,
      { silent }: SaveProactiveStatusOptions = {},
    ): Promise<boolean> => {
      const newStatus = enabled ? 'ACTIVE' : 'INACTIVE';

      try {
        const result = await client.mutate({
          mutation: TrelloUpdateSmartScheduleOnboardingProactiveStatusDocument,
          variables: {
            input: {
              status: newStatus,
              userId: memberAri,
            },
          },
          context: { traceId: null },
          optimisticResponse: {
            trello: {
              __typename: 'TrelloMutationApi',
              updateProactiveSmartScheduleStatus: {
                __typename: 'TrelloUpdateProactiveSmartScheduleStatusPayload',
                success: true,
                errors: null,
                member: {
                  __typename: 'TrelloMember',
                  id: memberAri,
                  aiPreferences: {
                    __typename: 'TrelloMemberAiPreference',
                    smartSchedule: {
                      __typename: 'TrelloSmartSchedulePreference',
                      status: newStatus,
                    },
                  },
                },
              },
            },
          },
        });

        assertMutationSuccess(
          result.data?.trello.updateProactiveSmartScheduleStatus,
          result.error,
          'Failed to update proactive schedule status',
        );

        return true;
      } catch {
        if (!silent) {
          showFlag({
            id: 'smartScheduleOnboardingProactiveStatusError',
            title:
              "We weren't able to update your proactive scheduling status. Please try again.",
            appearance: 'error',
            isAutoDismiss: false,
          });
        }

        return false;
      }
    },
    [memberAri],
  );

  return { saveBoardPreference, saveProactiveStatus };
};
